import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Divider,
  Typography
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { Helmet } from 'react-helmet';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import usePageTitle from '../../hooks/usePageTitle';
import { APP_PATH_SIGNUP } from '../../config';
import PricingTierCards from '../../components/PricingTierCards';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(12),
    background: theme.palette.background.default
  },
  heroSection: {
    textAlign: 'center',
    marginBottom: theme.spacing(5)
  },
  heroTitle: {
    fontWeight: 700,
    fontSize: '3rem',
    lineHeight: 1.2,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem'
    }
  },
  heroSubtitle: {
    fontSize: '1.25rem',
    color: theme.palette.text.secondary,
    maxWidth: '700px',
    margin: '0 auto',
    marginBottom: theme.spacing(1)
  },
  heroHighlight: {
    fontSize: '1.125rem',
    fontWeight: 600,
    marginTop: theme.spacing(2)
  },
  section: {
    marginBottom: theme.spacing(10)
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '2rem',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem'
    }
  },
  sectionSubtitle: {
    fontSize: '1.125rem',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto',
    marginBottom: theme.spacing(6)
  },
  featureList: {
    maxWidth: '600px',
    margin: '0 auto'
  },
  featureItem: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5)
  },
  featureIcon: {
    minWidth: 40,
    color: theme.palette.primary.main
  },
  featureText: {
    '& .MuiListItemText-primary': {
      fontSize: '1rem',
      fontWeight: 500,
      color: theme.palette.text.primary,
      lineHeight: 1.6
    }
  },
  profileSection: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}08 100%)`,
    padding: theme.spacing(6),
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(10)
  },
  profileTitle: {
    fontWeight: 600,
    fontSize: '1.75rem',
    marginBottom: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.primary
  },
  profileDescription: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    maxWidth: '700px',
    margin: '0 auto',
    marginBottom: theme.spacing(4)
  },
  profileTiers: {
    display: 'flex',
    gap: theme.spacing(3),
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(4)
  },
  profileTier: {
    padding: theme.spacing(2),
    flex: '1 1 200px',
    maxWidth: '250px',
    textAlign: 'center'
  },
  profileTierTitle: {
    fontWeight: 600,
    fontSize: '1.125rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  profileTierSavings: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1)
  },
  profileTierDescription: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary
  },
  benefitsSection: {
    marginBottom: theme.spacing(10)
  },
  benefitsGrid: {
    marginTop: theme.spacing(4)
  },
  benefitCard: {
    textAlign: 'center',
    padding: theme.spacing(3)
  },
  benefitIcon: {
    fontSize: '3rem',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2)
  },
  benefitTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  benefitDescription: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6
  },
  plansSection: {
    marginBottom: theme.spacing(10)
  },
  ctaSection: {
    textAlign: 'center',
    marginTop: theme.spacing(10),
    paddingTop: theme.spacing(8),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  ctaTitle: {
    fontWeight: 600,
    fontSize: '2rem',
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary
  },
  ctaText: {
    fontSize: '1.125rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4)
  },
  ctaButton: {
    padding: theme.spacing(1.5, 5),
    fontSize: '1.125rem',
    fontWeight: 600,
    textTransform: 'none'
  },
  dividerSpacing: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  reassuranceBox: {
    marginTop: theme.spacing(8),
    padding: theme.spacing(3),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}05 0%, ${theme.palette.success.main}05 100%)`,
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.success.main}30`,
    textAlign: 'center'
  },
  reassuranceTitle: {
    fontWeight: 600,
    fontSize: '1.125rem',
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1)
  },
  reassuranceText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6
  },
  faqSection: {
    marginBottom: theme.spacing(10)
  },
  faqTitle: {
    fontWeight: 600,
    fontSize: '2rem',
    marginBottom: theme.spacing(1),
    textAlign: 'left',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem'
    }
  },
  faqSubtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
    maxWidth: '700px'
  },
  faqItem: {
    background: 'transparent',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:before': {
      display: 'none'
    },
    '&.Mui-expanded': {
      margin: 0
    }
  },
  faqSummary: {
    padding: theme.spacing(2, 0),
    '& .MuiAccordionSummary-content': {
      margin: 0
    }
  },
  faqQuestion: {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  faqIcon: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.primary
  },
  faqDetails: {
    padding: theme.spacing(0, 0, 2, 0)
  },
  faqAnswer: {
    fontSize: '0.95rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    maxWidth: '760px'
  }
}));

const Pricing = () => {
  const classes = useStyles();
  const history = useHistory();
  const [selectedTier, setSelectedTier] = useState(null);
  const [brandColor, setBrandColor] = useState('#3f51b5');
  const [expandedFaq, setExpandedFaq] = useState(false);

  const faqs = [
    {
      question: 'How does pricing work?',
      answer:
        'You buy job posts in packs. There are no subscriptions, and you only pay when you are ready to publish.'
    },
    {
      question: 'Do job packs expire?',
      answer:
        'No. Job packs never expire, so you can publish whenever you are ready.'
    },
    {
      question: 'Can I share a pack across roles or teams?',
      answer:
        'Yes. Each pack is flexible and can be used across multiple roles and hiring managers.'
    },
    {
      question: 'What is included with each job post?',
      answer:
        'Every post includes the full hiring workspace: candidate pipeline, messaging, interview scheduling, and reporting.'
    },
    {
      question: 'Do you offer discounts for larger teams?',
      answer:
        'Yes. The Growth Pack is designed for high-volume hiring. Contact us if you need a custom plan.'
    }
  ];

  usePageTitle('Pricing - Remote Job');

  // Restore selected plan from sessionStorage when component mounts
  useEffect(() => {
    const storedPlan = sessionStorage.getItem('selectedJobPlan');
    if (storedPlan) {
      try {
        const plan = JSON.parse(storedPlan);
        // Set the selected tier ID (e.g., 'starter', 'professional', 'premium')
        // Map planId back to tier id
        const planIdToTierId = {
          9: 'starter',
          10: 'professional',
          11: 'premium'
        };
        const tierId = planIdToTierId[plan.planId];
        if (tierId) {
          setSelectedTier(tierId);
        }
        if (plan.brandColor) {
          setBrandColor(plan.brandColor);
        }
      } catch (e) {
        console.error('Failed to parse selected plan:', e);
      }
    }
  }, []);

  const handleTierSelect = () => {
    history.push(APP_PATH_SIGNUP);
  };

  const handleBrandColorChange = (color) => {
    setBrandColor(color.hex);
  };

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };
  return (
    <div className={classes.root}>
      <Helmet>
        <title>Pricing - One-Time Payment Per Job Post | Remote Job</title>
        <meta
          name="description"
          content="Simple, transparent pricing for job pack. Buy a pack and publish when you are ready. Hiring tools included."
        />
        <meta
          name="keywords"
          content="job posting pricing, remote job pricing, hiring platform cost, applicant tracking system pricing, no subscription job board"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Pricing - One-Time Payment Per Job Post | Remote Job"
        />
        <meta
          property="og:description"
          content="Simple, transparent pricing for job pack. Buy a pack and publish when you are ready."
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Pricing - Job Credit Packs | Remote Job"
        />
        <meta
          name="twitter:description"
          content="Simple, transparent pricing for job pack. Buy a pack and publish when you are ready."
        />
      </Helmet>

      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box className={classes.heroSection}>
          <Typography className={classes.heroTitle} variant="h1">
            Post Jobs. Hire Candidates. Pay Only When You Publish.
          </Typography>
          <Typography className={classes.heroSubtitle} variant="h5">
            Buy job posts in packs. No subscriptions, no contracts.
          </Typography>
          <Typography className={classes.heroHighlight}>
            Each job post includes the full hiring workspace.
          </Typography>
        </Box>
        <Divider className={classes.dividerSpacing} />

        {/* Job Plans Section */}
        <Box className={classes.plansSection}>
          <PricingTierCards
            selectedTier={selectedTier}
            onTierSelect={handleTierSelect}
            brandColor={brandColor}
            onBrandColorChange={handleBrandColorChange}
            jobRegionalRestrictions={[]}
            bundleOnly
          />

          {/* Reassurance Box */}
          <Box className={classes.reassuranceBox}>
            <Typography className={classes.reassuranceTitle}>
              <LockOpenIcon /> No Payment Required Until You Publish
            </Typography>
            <Typography className={classes.reassuranceText}>
              Create your account and set up your job posts first. You’ll review
              everything before entering payment details.
            </Typography>
          </Box>
        </Box>

        {/* Company FAQ Section */}
        <Box className={classes.faqSection}>
          <Typography className={classes.faqTitle} variant="h2">
            Frequently asked questions
          </Typography>
          <Typography className={classes.faqSubtitle}>
            Everything you need to know about job packs, usage, and what is
            included.
          </Typography>
          <Box>
            {faqs.map((faq, index) => {
              const panelId = `faq-${index}`;
              const isExpanded = expandedFaq === panelId;
              return (
                <Accordion
                  key={faq.question}
                  expanded={isExpanded}
                  onChange={handleFaqChange(panelId)}
                  className={classes.faqItem}
                  square>
                  <AccordionSummary
                    className={classes.faqSummary}
                    expandIcon={
                      <Box className={classes.faqIcon}>
                        {isExpanded ? (
                          <RemoveIcon fontSize="small" />
                        ) : (
                          <AddIcon fontSize="small" />
                        )}
                      </Box>
                    }>
                    <Typography className={classes.faqQuestion}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.faqDetails}>
                    <Typography className={classes.faqAnswer}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </Box>

        {/* Final CTA Section */}
        <Box className={classes.ctaSection}>
          <Typography className={classes.ctaTitle} variant="h2">
            Ready to Post a Job?
          </Typography>
          <Typography className={classes.ctaText}>
            Create your account and publish when you’re ready.
          </Typography>
          <Button
            component={RouterLink}
            to={APP_PATH_SIGNUP}
            variant="contained"
            color="primary"
            size="large"
            className={classes.ctaButton}>
            Start Hiring
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default Pricing;
